import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';

import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  constructor(private httpClient: HttpClient, private messageService: MessageService) {}

  /**
   *
   * @param file - file to be download
   */
  downloadFile(file: any): void {
    let filename: string = this.extractFileNameFromHeaders(file.headers.headers);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file.body);
    link.download = filename;

    link.click();
  }

  /**
   * For formatting dates
   * @param date - date to be formatted
   * @param format - required format
   * @param locale - Optional. Required locale.
   */
  formatDate(date: string | number | Date, format: string, locale: string = environment.locale): string {
    return formatDate(date, format, locale);
  }

  /**
   * For get file name from url
   * @param url - uploaded url
   */
  getFileName(url: string): string {
    return decodeURIComponent(url.split('?')[0].split('/')[4]);
  }

  /**
   * For formatting size
   * @param bytes - size in bytes
   * @param decimalPoints - Optional. Required number of decimal points. Default is 3.
   */
  formatSize(bytes: number, decimalPoints: number = 3): string {
    if (bytes === 0) {
      return '0 B';
    }

    const k = 1000;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(decimalPoints))} ${sizes[i]}`;
  }

  /**
   * For loading json data from assets
   * @param fileName - name of the file to be fetched
   */
  getJsonFromFile(fileName: string): Observable<any> {
    return this.httpClient.get<any>(`assets/json/${fileName}`);
  }

  imageBase64Decoder(decodeUrl: string) {
    const binaryString = atob(decodeUrl);

    // Create a Uint8Array from the binary data
    const uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the Uint8Array
    const blob = new Blob([uint8Array]);

    // Create a trusted URL from the Blob
    const decodedUrl = URL.createObjectURL(blob);

    return decodedUrl;
  }

  /**
   * For checking if keyboard event is of a number key or enter key
   * @param e - keyboard event
   */
  isNumberKey(e: KeyboardEvent): boolean {
    return ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Enter'].includes(e.key);
  }

  /**
   * For displaying global toast messages
   * @param type - message type. Options are 'info' | 'success' | 'error'
   * @param message - message to display
   * @param duration - Optional. Duration to show the message.
   */
  showMessage(
    type: 'info' | 'success' | 'error',
    message: string = '',
    duration?: number,
    clearOldMessages: boolean = true
  ): void {
    // TODO to be updated after translate service is added
    const summary = type.charAt(0).toUpperCase() + type.slice(1);
    // const summary = this.translateService.instant(`toast.${type}`)

    if (clearOldMessages) {
      this.messageService.clear();
    }

    this.messageService.add({
      detail: message,
      key: 'toastMessage',
      life: duration ?? environment.toast.duration[type],
      severity: type,
      summary: summary,
    });
  }

  extractFileNameFromHeaders(headers: any): string {
    const contentDispositionArray = headers.get('content-disposition');

    if (contentDispositionArray && contentDispositionArray.length > 0) {
      const contentDispositionValue = contentDispositionArray[0];
      const filenameMatch = contentDispositionValue.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?/);

      if (filenameMatch && filenameMatch[1]) {
        const filename = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes if present

        return filename;
      }
    }

    return 'unknown_filename';
  }
}
